// hooks/useFarms.js
import { useState, useEffect } from 'react';
import { ApiUrl } from '../../../../utils/ApiUrl';
import { FARMS_SORT_OPTIONS } from '../../../../utils/sortConfig';

const useFarms = (poolCounts) => {
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [sortBy, setSortBy] = useState(FARMS_SORT_OPTIONS[0]);
    const [rootPools, setRootPools] = useState([]);
    const [loader, setLoader] = useState(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(12);
    const [totalItems, setTotalItems] = useState(0);

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
        setCurrentPage(1); // Reset to first page on sort change
    };

    // Pagination handlers
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
        setCurrentPage(1);
    };

    // Debounce search value
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 600); // Wait 600ms after user stops typing

        return () => clearTimeout(timeoutId);
    }, [searchValue]);

    const fetchRootPools = async () => {
        try {
            setLoader(true);

            // const accessToken = localStorage.getItem('accessToken');

            // if (!accessToken) {
            //     console.error('No access token found');
            //     return;
            // }

            const offset =  (currentPage - 1) * perPage;
            let perPageStatic = poolCounts ? poolCounts : perPage;
            // Build URL with search and sort parameters
            let url = `${ApiUrl}/pool/root-pools?limit=${perPageStatic}&offset=${offset}`;

            if (debouncedSearchValue.trim()) {
                url += `&search=${encodeURIComponent(debouncedSearchValue.trim())}`;
            }

            if (sortBy?.value) {
                url += `&sort=${sortBy.value}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    // 'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result?.data?.pagination) {
                setTotalItems(result.data.pagination.total);
            }

            const transformedPools = result?.data?.items?.map(pool => ({
                id: pool._id,
                projectName: pool.projectName?.replace(/,/g, '').trim() || 'Unnamed Pool',
                logo: pool.projectLogo || '/assets/pool.svg',
                rewardAmount: pool.rewardAmount || 0,
                childCount: pool.childPoolCount || 0,
                apr: pool.apr || 0,
                contractHash: pool.contractHash,
                unifarm: pool.contractHash,
                chain: pool.chain?.replace(/,/g, '').trim(),
                tagline: pool.tagline?.replace(/,/g, '').trim(),
                poolDescription: pool.poolDescription?.replace(/,/g, '').trim(),
                socials: pool.socials,
                poolEndDate: pool.poolEndDate,
                gasFees: pool.gasFees,
                walletAddress: pool.walletAddress?.replace(/,/g, '').trim(),
                rewardToken: pool.rewardToken,
                link: `/activepools/${pool._id}`,
                contractAddress: pool.contractAddress,
                ...pool
            })) || [];

            setRootPools(transformedPools);
            // console.log('Root pools fetched:', transformedPools);
        } catch (error) {
            console.error('Error fetching root pools:', error);
            setRootPools([]);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (poolCounts) {
          setTimeout(() => {
              fetchRootPools();
          }, 2000);
        }else{
            fetchRootPools();
        }
    }, [currentPage, perPage, debouncedSearchValue, sortBy, poolCounts]);

    return {
        // State
        searchValue,
        sortBy,
        rootPools,
        loader,
        currentPage,
        perPage,
        totalItems,

        // Handlers
        handleSearchChange,
        handleSortChange,
        handlePageChange,
        handlePerPageChange,

        // Functions
        fetchRootPools
    };
};

export default useFarms;